import {
  getAgentInstructionChecksToolOutputSchema,
  type AgentInstructionChecksLlmOutput,
  type StaticAgentSectionKey,
} from "@repo/app-types/entities/configurationAgent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useMastraChatContext } from "../mastra/mastraChatContext.js";
import type { TeammateAgentConfigController } from "../useTeammateAgentConfigController.js";

/** The registration key of the Mastra tool (matches the chat message part type). */
const CHECKS_TOOL_NAME = "getAgentInstructionChecksTool";

/**
 * The canned user turn the "Test" button sends. The configuration agent is
 * documented to call `getAgentInstructionChecksTool` (no candidate) in response.
 */
const RUN_CHECKS_MESSAGE =
  "Test my instruction sections: run the automated instruction section checks now using the instruction-checks tool, then give me a brief takeaway of what failed.";

/** A nonce-gated request to navigate the Instructions tab to a section. */
export interface SectionSelectionRequest {
  section: StaticAgentSectionKey;
  /** Bumped on each request so re-selecting the same section re-fires. */
  nonce: number;
}

export interface SectionChecksContextValue {
  /** Latest all-sections check result, or null before any run this session. */
  checks: AgentInstructionChecksLlmOutput | null;
  /** True from a Test click until the triggered turn finishes. */
  isChecking: boolean;
  /** True while any assistant turn is in flight (checks or chat). */
  isRunning: boolean;
  /** Flush pending draft edits, then ask the agent to run the checks. */
  runChecks: () => void;
}

export interface SectionSuggestionsContextValue {
  /** Proposed replacement content per section (latest proposal wins). */
  suggestions: Partial<Record<StaticAgentSectionKey, string>>;
  /** Latest navigate-to-section request from the suggestion list. */
  selectionRequest: SectionSelectionRequest | null;
  /** Sections currently visible in the editor (drives the list "Viewing" flag). */
  viewedSections: readonly StaticAgentSectionKey[];
  /** Ask the Instructions tab to navigate to (and focus) a section. */
  requestSelectSection: (section: StaticAgentSectionKey) => void;
  /** Report which sections the editor is currently showing. */
  setViewedSections: (sections: readonly StaticAgentSectionKey[]) => void;
  /**
   * Apply every pending proposal among the given sections to the draft in a
   * single change, then clear them. Applying in one change (rather than per
   * field) is required for multi-field sections like Identity — see the
   * implementation note.
   */
  applySuggestions: (sections: readonly StaticAgentSectionKey[]) => void;
  /** Dismiss a proposal without changing the draft. */
  discardSuggestion: (section: StaticAgentSectionKey) => void;
}

const SectionChecksContext = createContext<SectionChecksContextValue | null>(
  null,
);
const SectionSuggestionsContext =
  createContext<SectionSuggestionsContextValue | null>(null);

export interface SectionTestingProviderProps {
  controller: TeammateAgentConfigController;
  children: ReactNode;
}

/**
 * Client-only store for the AI Assistant's instruction-section testing flow.
 * The agent's `getAgentInstructionChecksTool` output bundles both the pass/fail
 * `checks` and the proposed `candidate_draft`, so one turn-complete harvest of
 * that output feeds both surfaces — the Results checks and the inline
 * suggestions (diff + Apply/Discard) — mirroring the playbook editor, where a
 * single sample-tool result drives both. The "Test" button flushes the draft
 * and sends a canned message (checks the saved draft, no candidate); the agent
 * proposing an edit calls the same tool with a `candidate_draft`.
 *
 * Session-scoped (lost on reload) and cleared on publish/discard, exactly like
 * the playbook editor's results.
 */
export const SectionTestingProvider: FC<SectionTestingProviderProps> = ({
  controller,
  children,
}) => {
  const chat = useMastraChatContext();
  const { agentConfig, flushAgentConfigSave } = controller;
  // `agentConfig` is a fresh object each render, but `onChange` is a stable
  // `useCallback([])` — destructure it so callbacks can depend on it directly
  // without re-creating every render.
  const { onChange: onAgentConfigChange } = agentConfig;

  const [checks, setChecks] = useState<AgentInstructionChecksLlmOutput | null>(
    null,
  );
  const [isChecking, setIsChecking] = useState(false);
  const [suggestions, setSuggestions] = useState<
    Partial<Record<StaticAgentSectionKey, string>>
  >({});
  const [selectionRequest, setSelectionRequest] =
    useState<SectionSelectionRequest | null>(null);
  const [viewedSections, setViewedSectionsState] = useState<
    readonly StaticAgentSectionKey[]
  >([]);

  // Discard both checks and pending proposals once the draft is published or
  // discarded, matching the playbook editor (which clears its Results then). The
  // flag goes `true → false` only on a successful publish/discard; edits and
  // failed publishes keep it `true`, so this fires exactly on settle.
  const prevHasUnpublishedRef = useRef(controller.hasUnpublishedChanges);
  useEffect(() => {
    const justSettled =
      prevHasUnpublishedRef.current && !controller.hasUnpublishedChanges;
    prevHasUnpublishedRef.current = controller.hasUnpublishedChanges;
    if (justSettled) {
      setChecks(null);
      setSuggestions({});
    }
  }, [controller.hasUnpublishedChanges]);

  // Read the latest draft sections inside the collector without re-subscribing
  // the turn-completion effect to every keystroke.
  const sectionsRef = useRef(agentConfig.data?.sections ?? null);
  sectionsRef.current = agentConfig.data?.sections ?? null;

  // Harvest the checks tool output ONCE when a turn finishes. Its output bundles
  // the checks and the proposed candidate_draft, so a single scan feeds both.
  // Each assistant message is processed once, so applied/discarded proposals and
  // already-seen checks don't reappear.
  const prevIsRunningRef = useRef(false);
  const processedMessageIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const justFinished = prevIsRunningRef.current && !chat.isRunning;
    prevIsRunningRef.current = chat.isRunning;
    if (!justFinished) {
      return;
    }

    // Any completed turn ends the "checking" state (the checks turn, or an
    // unrelated chat turn that finished after we armed it).
    setIsChecking(false);

    const lastMessage = chat.messages.at(-1);
    if (!lastMessage || lastMessage.role !== "assistant") {
      return;
    }
    if (processedMessageIdsRef.current.has(lastMessage.id)) {
      return;
    }
    processedMessageIdsRef.current.add(lastMessage.id);

    let harvestedChecks: AgentInstructionChecksLlmOutput | null = null;
    const candidateDrafts: Partial<Record<StaticAgentSectionKey, string>> = {};
    for (const part of lastMessage.parts) {
      // AI SDK tool parts are `dynamic-tool` or `tool-${name}`.
      const toolName =
        part.type === "dynamic-tool"
          ? part.toolName
          : part.type.startsWith("tool-")
            ? part.type.slice("tool-".length)
            : null;
      if (toolName !== CHECKS_TOOL_NAME) {
        continue;
      }
      if (!("state" in part) || part.state !== "output-available") {
        continue;
      }
      const parsed = getAgentInstructionChecksToolOutputSchema.safeParse(
        (part as { output?: unknown }).output,
      );
      if (!parsed.success) {
        continue;
      }
      harvestedChecks = parsed.data.checks;
      if (parsed.data.candidate_draft) {
        Object.assign(candidateDrafts, parsed.data.candidate_draft);
      }
    }

    if (harvestedChecks) {
      setChecks(harvestedChecks);
    }

    const proposedSections = Object.entries(candidateDrafts) as [
      StaticAgentSectionKey,
      string,
    ][];
    if (proposedSections.length > 0) {
      setSuggestions((prev) => {
        const current = sectionsRef.current;
        const next = { ...prev };
        for (const [section, content] of proposedSections) {
          // A candidate identical to the current draft is a check-only run (the
          // tool validated the section as-is): surface no suggestion, so the
          // editor shows just the Results checks with no diff / Apply-Discard.
          // Trim-tolerant so a whitespace-only echo can't create a phantom diff.
          if (current && (current[section] ?? "").trim() === content.trim()) {
            continue;
          }
          next[section] = content;
        }
        return next;
      });
    }
  }, [chat.isRunning, chat.messages]);

  const runChecks = useCallback(() => {
    if (chat.isRunning || isChecking) {
      return;
    }
    setIsChecking(true);
    void (async () => {
      try {
        // Persist pending draft edits so the server-side tool checks exactly
        // what's on screen (mirrors the "Try this agent" flush).
        const saved = await flushAgentConfigSave();
        if (!saved) {
          setIsChecking(false);
          return;
        }
        chat.sendMessage(RUN_CHECKS_MESSAGE);
      } catch {
        setIsChecking(false);
      }
    })();
  }, [chat, isChecking, flushAgentConfigSave]);

  const requestSelectSection = useCallback((section: StaticAgentSectionKey) => {
    setSelectionRequest((prev) => ({
      section,
      nonce: (prev?.nonce ?? 0) + 1,
    }));
  }, []);

  const setViewedSections = useCallback(
    (sections: readonly StaticAgentSectionKey[]) => {
      setViewedSectionsState(sections);
    },
    [],
  );

  const discardSuggestion = useCallback((section: StaticAgentSectionKey) => {
    setSuggestions((prev) => {
      if (prev[section] === undefined) {
        return prev;
      }
      const next = { ...prev };
      delete next[section];
      return next;
    });
  }, []);

  const applySuggestions = useCallback(
    (sections: readonly StaticAgentSectionKey[]) => {
      // Read the current draft sections once (from the ref, kept in sync during
      // render) and fold every pending field into a SINGLE change. Applying
      // per field in a loop would re-read the same stale snapshot — there is no
      // re-render between synchronous calls — and, because `onChange` replaces
      // the whole `sections` object, each call would clobber the previous
      // field. This matters for the Identity section, which spans two fields.
      const current = sectionsRef.current;
      if (!current) {
        return;
      }
      const updates: Partial<Record<StaticAgentSectionKey, string>> = {};
      for (const section of sections) {
        const content = suggestions[section];
        if (content !== undefined) {
          updates[section] = content;
        }
      }
      const appliedSections = Object.keys(updates) as StaticAgentSectionKey[];
      if (appliedSections.length === 0) {
        return;
      }
      onAgentConfigChange({ sections: { ...current, ...updates } });
      setSuggestions((prev) => {
        const next = { ...prev };
        for (const section of appliedSections) {
          delete next[section];
        }
        return next;
      });
    },
    [suggestions, onAgentConfigChange],
  );

  // Two separately-memoized values behind two contexts, so a checks-only change
  // doesn't re-render suggestion-only consumers (and vice versa).
  const checksValue = useMemo<SectionChecksContextValue>(
    () => ({
      checks,
      isChecking,
      isRunning: chat.isRunning,
      runChecks,
    }),
    [checks, isChecking, chat.isRunning, runChecks],
  );

  const suggestionsValue = useMemo<SectionSuggestionsContextValue>(
    () => ({
      suggestions,
      selectionRequest,
      viewedSections,
      requestSelectSection,
      setViewedSections,
      applySuggestions,
      discardSuggestion,
    }),
    [
      suggestions,
      selectionRequest,
      viewedSections,
      requestSelectSection,
      setViewedSections,
      applySuggestions,
      discardSuggestion,
    ],
  );

  return (
    <SectionChecksContext.Provider value={checksValue}>
      <SectionSuggestionsContext.Provider value={suggestionsValue}>
        {children}
      </SectionSuggestionsContext.Provider>
    </SectionChecksContext.Provider>
  );
};

export function useSectionChecks(): SectionChecksContextValue {
  const context = useContext(SectionChecksContext);
  if (!context) {
    throw new Error(
      "useSectionChecks must be used within a SectionTestingProvider",
    );
  }
  return context;
}

export function useSectionSuggestions(): SectionSuggestionsContextValue {
  const context = useContext(SectionSuggestionsContext);
  if (!context) {
    throw new Error(
      "useSectionSuggestions must be used within a SectionTestingProvider",
    );
  }
  return context;
}
